import React, { useState } from "react";
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, TextField,
} from "@mui/material";
import { FraudFlag, FraudParty, FraudStatus, reviewFraudFlag } from "../../services/fraudService";
import { FRAUD_NOTE_MAX, FRAUD_NOTE_MIN, FRAUD_PARTIES, isValidFraudNote } from "../../utils/fraud";
import { getApiErrorMessage } from "../../lib/toast";
import { useToast } from "../../lib/useToast";

const COPY: Record<FraudStatus, { title: string; text: string; submit: string; done: string; color: "success" | "error" | "primary" }> = {
    CLEARED: {
        title: "Clear ride",
        text: "Not fraud. The ride moves to Cleared with your name and note.",
        submit: "Clear ride",
        done: "cleared",
        color: "success",
    },
    CONFIRMED: {
        title: "Confirm fraud on ride",
        text: "This ride was fraud. It moves to Confirmed with your name, note and who was responsible. Billing is not changed.",
        submit: "Confirm fraud",
        done: "marked as confirmed fraud",
        color: "error",
    },
    OPEN: {
        title: "Re-open ride",
        text: "Back to Open for another look. The current verdict, its note and who was responsible are removed.",
        submit: "Re-open ride",
        done: "re-opened",
        color: "primary",
    },
};

interface FraudReviewActionsProps {
    rideId: number;
    /** OPEN offers Clear / Confirm, anything else Re-open; the shares and explanation feed the Confirm checkboxes. */
    flag: Pick<FraudFlag, "status" | "driverPct" | "agentPct" | "riderPct" | "explanation">;
    /** Called after a verdict is saved so the caller can reload. */
    onChanged: () => unknown;
}

/** Clear / Confirm / Re-open with the note dialog. Render only for fraud:review holders. */
const FraudReviewActions: React.FC<FraudReviewActionsProps> = ({ rideId, flag, onChanged }) => {
    const toast = useToast();
    const [target, setTarget] = useState<FraudStatus | null>(null);
    const [note, setNote] = useState("");
    const [responsible, setResponsible] = useState<FraudParty[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Agent is only a choice when the ride had one.
    const parties = FRAUD_PARTIES.filter((p) => p.key !== "AGENT" || flag.agentPct !== null);

    const openDialog = (next: FraudStatus) => {
        const likely = flag.explanation.mostLikely?.toUpperCase();
        setTarget(next);
        setNote("");
        setError(null);
        setResponsible(parties.filter((p) => p.key === likely).map((p) => p.key));
    };

    const close = () => {
        if (!busy) setTarget(null);
    };

    const toggle = (key: FraudParty) =>
        setResponsible((r) => (r.includes(key) ? r.filter((k) => k !== key) : [...r, key]));

    const submit = async (next: FraudStatus) => {
        setBusy(true);
        setError(null);
        try {
            const who = next === "CONFIRMED" ? parties.map((p) => p.key).filter((k) => responsible.includes(k)) : [];
            await reviewFraudFlag(rideId, next, note.trim(), who);
            setTarget(null);
            toast.success(`Ride #${rideId} ${COPY[next].done}`);
            await onChanged();
        } catch (e) {
            setError(getApiErrorMessage(e, "Could not save the review"));
        } finally {
            setBusy(false);
        }
    };

    const copy = COPY[target ?? "OPEN"];
    const length = note.trim().length;
    const ready = isValidFraudNote(target ?? "OPEN", note) && (target !== "CONFIRMED" || responsible.length > 0);

    return (
        <>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {flag.status === "OPEN" ? (
                    <>
                        <Button size="small" variant="outlined" color="success" onClick={() => openDialog("CLEARED")}>
                            Clear
                        </Button>
                        <Button size="small" variant="contained" color="error" onClick={() => openDialog("CONFIRMED")}>
                            Confirm
                        </Button>
                    </>
                ) : (
                    <Button size="small" variant="outlined" onClick={() => openDialog("OPEN")}>
                        Re-open
                    </Button>
                )}
            </Box>

            {target && (
                <Dialog open onClose={close} fullWidth maxWidth="xs">
                    <DialogTitle>{copy.title} #{rideId}</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>{copy.text}</DialogContentText>
                        <TextField
                            label={target === "OPEN" ? "Note (optional)" : "Note"}
                            fullWidth multiline minRows={2} autoFocus
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            inputProps={{ maxLength: FRAUD_NOTE_MAX }}
                            helperText={target === "OPEN"
                                ? `${length}/${FRAUD_NOTE_MAX}`
                                : `${length}/${FRAUD_NOTE_MAX} — at least ${FRAUD_NOTE_MIN} characters`}
                        />
                        {target === "CONFIRMED" && (
                            <FormControl component="fieldset" sx={{ mt: 2 }}>
                                <FormLabel component="legend">Who was responsible?</FormLabel>
                                <FormGroup row>
                                    {parties.map((p) => (
                                        <FormControlLabel
                                            key={p.key}
                                            control={<Checkbox checked={responsible.includes(p.key)} onChange={() => toggle(p.key)} />}
                                            label={typeof flag[p.share] === "number" ? `${p.label} (${flag[p.share]}%)` : p.label}
                                        />
                                    ))}
                                </FormGroup>
                                <FormHelperText>Tick at least one.</FormHelperText>
                            </FormControl>
                        )}
                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={close} disabled={busy}>Cancel</Button>
                        <Button
                            variant="contained"
                            color={copy.color}
                            disabled={busy || !ready}
                            onClick={() => submit(target)}
                            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
                        >
                            {copy.submit}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

export default FraudReviewActions;
